import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useContract } from '../hooks/useContract';
import { useNotification } from './NotificationSystem';
import { formatAddress, formatUSDT, formatNumber, generateReferralLink, getReferralIdFromURL, PACKAGE_NAMES, PACKAGE_PRICES } from '../utils/constants';

const Dashboard = ({ account, provider, signer, network }) => {
  const { 
    userData, 
    packageData, 
    poolData, 
    loading, 
    loadUserData,
    refreshUserData,
    register,
    upgradePackage,
    retopupPackage,
    claimIncome,
    claimAllPoolIncomes,
    getUserId
  } = useContract(provider, signer, network);

  const { notifySuccess, notifyError, notifyInfo, notifyTransaction } = useNotification();

  // State management
  const [userRegistered, setUserRegistered] = useState(null);
  const [referralId, setReferralId] = useState('');
  const [registering, setRegistering] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [upgrading, setUpgrading] = useState({});
  const [retoping, setRetoping] = useState({});
  const [referralLink, setReferralLink] = useState('');
  
  // Use refs to prevent duplicate calls
  const hasCheckedRef = useRef(false);
  const currentAccountRef = useRef(null);

  // Registration check
  useEffect(() => {
    // Reset state when account changes
    if (currentAccountRef.current !== account) {
      console.log('Account changed, resetting state');
      hasCheckedRef.current = false;
      currentAccountRef.current = account;
      setUserRegistered(null);
      setReferralLink('');
      setReferralId('');
    }

    // Skip if already checked or missing dependencies
    if (!account || !provider || !getUserId || hasCheckedRef.current) {
      return;
    }

    const checkRegistration = async () => {
      console.log('Starting registration check for:', account);
      hasCheckedRef.current = true;
      
      try {
        // Check if user is registered
        const userId = await getUserId(account);
        console.log('Registration check result:', { userId });
        
        if (userId && userId !== '0') {
          console.log('User is registered');
          setUserRegistered(true);
          setReferralLink(generateReferralLink(userId));
          
          // Load user data
          if (loadUserData) {
            await loadUserData(account);
          }
          
          notifyInfo('Welcome back to 7Universe!');
        } else {
          console.log('User not registered');
          setUserRegistered(false);
          
          // Auto-fill referral ID from URL
          const urlReferralId = getReferralIdFromURL();
          if (urlReferralId) {
            setReferralId(urlReferralId);
            notifyInfo(`Referral ID ${urlReferralId} auto-filled from link`);
          }
        }
      } catch (error) {
        console.error('Registration check failed:', error);
        setUserRegistered(false);
        notifyError('Failed to check registration status');
      }
    };

    // Small delay to ensure wallet connection is stable
    const timer = setTimeout(checkRegistration, 1000);
    return () => clearTimeout(timer);
  }, [account, provider, getUserId, loadUserData, notifyInfo, notifyError]);

  const handleRegister = async () => {
    setRegistering(true);
    
    try {
      const finalReferralId = referralId.trim() || '100000';
      
      console.log('Registering user with referral ID:', finalReferralId);
      
      notifyTransaction.pending('Registration in progress...');
      
      await register(finalReferralId);
      
      notifySuccess('Registration successful! Welcome to 7Universe!');
      
      // Reset state to trigger re-check
      hasCheckedRef.current = false;
      setUserRegistered(null);
    } catch (error) {
      console.error('Registration failed:', error);
      notifyError(`Registration failed: ${error.message}`);
    } finally {
      setRegistering(false);
    }
  };

  const handleUpgrade = useCallback(async (packageId) => {
    setUpgrading(prev => ({ ...prev, [packageId]: true }));
    
    try {
      notifyTransaction.pending(`Upgrading to U${packageId}...`);
      await upgradePackage(packageId);
      notifySuccess(`Package U${packageId} upgraded successfully!`);
      setTimeout(() => refreshUserData && refreshUserData(account), 2000);
    } catch (error) {
      console.error('Upgrade failed:', error);
      notifyError(`Upgrade failed: ${error.message}`);
    } finally {
      setUpgrading(prev => ({ ...prev, [packageId]: false }));
    }
  }, [upgradePackage, refreshUserData, account, notifySuccess, notifyError, notifyTransaction]);

  const handleRetopup = useCallback(async (packageId) => {
    setRetoping(prev => ({ ...prev, [packageId]: true }));
    
    try {
      notifyTransaction.pending(`Retopup U${packageId}...`);
      await retopupPackage(packageId);
      notifySuccess(`Package U${packageId} retopup successful!`);
      setTimeout(() => refreshUserData && refreshUserData(account), 2000);
    } catch (error) {
      console.error('Retopup failed:', error);
      notifyError(`Retopup failed: ${error.message}`);
    } finally {
      setRetoping(prev => ({ ...prev, [packageId]: false }));
    }
  }, [retopupPackage, refreshUserData, account, notifySuccess, notifyError, notifyTransaction]);

  const handleClaim = useCallback(async () => {
    setClaiming(true);
    
    try {
      notifyTransaction.pending('Claiming income...');
      await claimIncome();
      notifySuccess('Income claimed successfully!');
      setTimeout(() => refreshUserData && refreshUserData(account), 2000);
    } catch (error) {
      console.error('Claim failed:', error);
      notifyError(`Claim failed: ${error.message}`);
    } finally {
      setClaiming(false);
    }
  }, [claimIncome, refreshUserData, account, notifySuccess, notifyError, notifyTransaction]);

  const handleClaimPools = useCallback(async () => {
    setClaiming(true);
    
    try {
      notifyTransaction.pending('Claiming pool incomes...');
      await claimAllPoolIncomes();
      notifySuccess('Pool incomes claimed successfully!');
      setTimeout(() => refreshUserData && refreshUserData(account), 2000);
    } catch (error) {
      console.error('Pool claim failed:', error);
      notifyError(`Pool claim failed: ${error.message}`);
    } finally {
      setClaiming(false);
    }
  }, [claimAllPoolIncomes, refreshUserData, account, notifySuccess, notifyError, notifyTransaction]);

  const copyReferralLink = useCallback(() => {
    navigator.clipboard.writeText(referralLink);
    notifySuccess('Referral link copied to clipboard!');
  }, [referralLink, notifySuccess]);

  const getHighestActivePackage = () => {
    if (!packageData || packageData.length === 0) return 'U1';
    const activePackages = packageData.filter(p => p.isActive);
    if (activePackages.length === 0) return 'U1';
    const highestId = Math.max(...activePackages.map(p => p.id));
    return `U${highestId}`;
  };

  // Show loading state
  if (userRegistered === null) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Checking registration status...</p>
      </div>
    );
  }

  // Registration screen
  if (userRegistered === false) {
    return (
      <div className="registration-container">
        <div className="registration-card">
          <div className="registration-form">
            <div className="registration-header">
              <h2>Join the 7Universe Matrix</h2>
              <p>Start your journey in the ultimate DeFi opportunity</p>
            </div>
            
            <div className="input-group">
              <label>Referral ID (Optional)</label>
              <input
                type="text"
                value={referralId}
                onChange={(e) => setReferralId(e.target.value)}
                placeholder="Enter referral ID or leave empty"
              />
              <small>Leave empty to use default referrer</small>
            </div>
            
            <div className="price-info">
              <div className="price-breakdown">
                <div className="price-item">
                  <span>Registration Fee:</span>
                  <span className="price">10 USDT</span>
                </div>
              </div>
            </div>
            
            <button 
              className="register-btn"
              onClick={handleRegister}
              disabled={registering}
            >
              {registering ? 'Registering...' : 'Join 7Universe (10 USDT)'}
            </button>
            
            <div className="security-note">
              <p>Secure transaction powered by OpBNB Network</p>
              <p>Supports MetaMask, TrustWallet, SafePal, Binance Wallet & more</p>
            </div>
          </div>
          
          <div className="registration-benefits">
            <h3>What You Get:</h3>
            <div className="benefits-grid">
              <div className="benefit-item">
                <div className="benefit-icon">🚀</div>
                <div>
                  <h4>U1 Package Activated</h4>
                  <p>Start with 10 USDT investment in the matrix system</p>
                </div>
              </div>
              <div className="benefit-item">
                <div className="benefit-icon">💰</div>
                <div>
                  <h4>Multiple Income Streams</h4>
                  <p>Sponsor bonuses, level income, and pool rewards</p>
                </div>
              </div>
              <div className="benefit-item">
                <div className="benefit-icon">⭐</div>
                <div>
                  <h4>Matrix Position</h4>
                  <p>Strategic placement in our binary matrix system</p>
                </div>
              </div>
              <div className="benefit-item">
                <div className="benefit-icon">🎯</div>
                <div>
                  <h4>Pool Eligibility</h4>
                  <p>Access to Galaxy, Cosmos, Universe & Eternal pools</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Dashboard content - user is registered
  return (
    <div className="dashboard">
      {/* Hero Section with Compact User Details */}
      <section className="hero-section-new">
        <div className="cosmic-grid">
          <div className="cosmic-orb"></div>
          <div className="cosmic-orb secondary"></div>
        </div>
        <div className="hero-content-new">
          <div className="welcome-badge">
            <span className="badge-icon">🌌</span>
            <span>Welcome to the Universe</span>
          </div>
          <h2 className="universe-title">Your Cosmic Dashboard</h2>
          
          {/* Compact User Details */}
          <div className="user-details-compact">
            <div className="detail-item">
              <div className="detail-icon">🆔</div>
              <div className="detail-content">
                <div className="detail-label">User ID</div>
                <div className="detail-value">{userData?.id || 'Loading...'}</div>
              </div>
            </div>
            <div className="detail-item">
              <div className="detail-icon">💛</div>
              <div className="detail-content">
                <div className="detail-label">Wallet</div>
                <div className="detail-value">{formatAddress(account)}</div>
              </div>
            </div>
            <div className="detail-item">
              <div className="detail-icon">⬆️</div>
              <div className="detail-content">
                <div className="detail-label">Upline ID</div>
                <div className="detail-value">{userData?.referrer || 'None'}</div>
              </div>
            </div>
            <div className="detail-item">
              <div className="detail-icon">📦</div>
              <div className="detail-content">
                <div className="detail-label">Package</div>
                <div className="detail-value">{getHighestActivePackage()}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Claim Section */}
      <section className="claim-section-new">
        <div className="claim-container">
          <div className="claim-background">
            <div className="floating-particles"></div>
          </div>
          <div className="claim-content">
            <div className="claim-header">
              <div className="claim-icon-wrapper">
                <span className="claim-icon">💰</span>
                <div className="pulse-ring"></div>
              </div>
              <div className="claim-text">
                <h3>Withdrawable Balance</h3>
                <p>Ready to claim your cosmic rewards</p>
              </div>
            </div>
            <div className="claim-amount-section">
              <div className="amount-display">
                <span className="currency">$</span>
                <span className="amount-value">
                  {formatNumber(userData?.claimableAmount || '0')}
                </span>
                <span className="currency-label">USDT</span>
              </div>
              <button 
                className="cosmic-claim-btn"
                onClick={handleClaim}
                disabled={claiming || !userData?.claimableAmount || parseFloat(userData.claimableAmount) <= 0}
              >
                <span className="btn-text">
                  {claiming ? 'Claiming...' : 'Claim Rewards'}
                </span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Referral Link Section */}
      {referralLink && (
        <section className="referral-section">
          <div className="referral-card">
            <div className="referral-header">
              <h3>Your Referral Link</h3>
              <p>Share this link to invite others and earn commissions</p>
            </div>
            <div className="referral-link-container">
              <div className="referral-input-wrapper">
                <input type="text" value={referralLink} readOnly className="referral-input" />
                <button className="copy-btn" onClick={copyReferralLink}>
                  <span>Copy</span>
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Statistics Grid */}
      <section className="stats-section">
        <h3 className="section-title">Statistics Overview</h3>
        <div className="stats-grid">
          <div className="stat-card">
            <h4>Total Income</h4>
            <span className="stat-value">{formatUSDT(userData?.totalIncome || '0')}</span>
          </div>
          <div className="stat-card">
            <h4>Total Withdraw</h4>
            <span className="stat-value">{formatUSDT(userData?.totalClaimed || '0')}</span>
          </div>
          <div className="stat-card">
            <h4>Direct Referrals</h4>
            <span className="stat-value">{userData?.totalDirectReferrals || '0'}</span>
          </div>
          <div className="stat-card">
            <h4>Claimable</h4>
            <span className="stat-value">{formatUSDT(userData?.claimableAmount || '0')}</span>
          </div>
        </div>
      </section>

      {/* Income Breakdown */}
      <section className="income-breakdown-section">
        <h3 className="section-title">Income Breakdown</h3>
        <div className="breakdown-card">
          <div className="breakdown-grid">
            <div className="breakdown-item">
              <div className="breakdown-label">Sponsor Income</div>
              <div className="breakdown-value">{formatUSDT(userData?.sponsorIncome || '0')}</div>
            </div>
            <div className="breakdown-item">
              <div className="breakdown-label">Level Income</div>
              <div className="breakdown-value">{formatUSDT(userData?.levelIncome || '0')}</div>
            </div>
            <div className="breakdown-item">
              <div className="breakdown-label">Galaxy Pool</div>
              <div className="breakdown-value">{formatUSDT(userData?.galaxyPoolIncome || '0')}</div>
            </div>
            <div className="breakdown-item">
              <div className="breakdown-label">Cosmos Pool</div>
              <div className="breakdown-value">{formatUSDT(userData?.cosmosPoolIncome || '0')}</div>
            </div>
            <div className="breakdown-item">
              <div className="breakdown-label">Universe Pool</div>
              <div className="breakdown-value">{formatUSDT(userData?.universePoolIncome || '0')}</div>
            </div>
            <div className="breakdown-item">
              <div className="breakdown-label">Eternal Pool</div>
              <div className="breakdown-value">{formatUSDT(userData?.eternalPoolIncome || '0')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Package Status */}
      <section className="packages-section">
        <h3 className="section-title">Package Status</h3>
        <div className="packages-grid">
          {packageData?.map((pkg, index) => (
            <div key={pkg.id} className={`package-card ${pkg.isActive ? 'active' : 'inactive'}`}>
              <div className="package-header">
                <div className="package-icon">
                  {pkg.isActive ? '✅' : '⭕'}
                </div>
                <div className="package-info">
                  <h4>{PACKAGE_NAMES[index]}</h4>
                  <span className="package-price">{PACKAGE_PRICES[`U${pkg.id}`]} USDT</span>
                </div>
              </div>
              <div className="package-details">
                <div className="detail-row">
                  <span>Status:</span>
                  <span className={pkg.isActive ? 'active-status' : 'inactive-status'}>
                    {pkg.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                {pkg.isActive && (
                  <>
                    <div className="detail-row">
                      <span>Invested:</span>
                      <span>{formatUSDT(pkg.totalInvested)}</span>
                    </div>
                    <div className="detail-row">
                      <span>Earned:</span>
                      <span>{formatUSDT(pkg.totalEarned)}</span>
                    </div>
                    <div className="detail-row">
                      <span>Remaining:</span>
                      <span>{formatUSDT(pkg.remainingLimit)}</span>
                    </div>
                  </>
                )}
              </div>
              <div className="package-actions">
                {!pkg.isActive && index > 0 && packageData[index - 1]?.isActive && (
                  <button
                    className="upgrade-btn"
                    onClick={() => handleUpgrade(pkg.id)}
                    disabled={upgrading[pkg.id]}
                  >
                    {upgrading[pkg.id] ? 'Upgrading...' : 'Upgrade'}
                  </button>
                )}
                {pkg.isActive && (
                  <button
                    className="retopup-btn"
                    onClick={() => handleRetopup(pkg.id)}
                    disabled={retoping[pkg.id]}
                  >
                    {retoping[pkg.id] ? 'Processing...' : 'Retopup'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Pool Details */}
      <section className="pools-section">
        <div className="pools-header">
          <h3 className="section-title">Pool Details</h3>
          <button 
            className="claim-all-pools-btn"
            onClick={handleClaimPools}
            disabled={claiming}
          >
            {claiming ? 'Claiming...' : 'Claim All Pools'}
          </button>
        </div>
        <div className="pools-grid">
          <div className={`pool-card ${poolData?.galaxy?.eligible ? 'eligible' : 'not-eligible'}`}>
            <div className="pool-header">
              <div className="pool-icon">🌌</div>
              <h4>Galaxy Pool</h4>
            </div>
            <div className="pool-info">
              <div className="pool-status">
                <span>Status: </span>
                <span className={poolData?.galaxy?.eligible ? 'eligible' : 'not-eligible'}>
                  {poolData?.galaxy?.eligible ? 'Eligible' : 'Not Eligible'}
                </span>
              </div>
              <div className="pool-claimable">
                <span>Claimable: </span>
                <span className="claimable-amount">{formatUSDT(poolData?.galaxy?.claimable || '0')}</span>
              </div>
            </div>
          </div>

          <div className={`pool-card ${poolData?.cosmos?.eligible ? 'eligible' : 'not-eligible'}`}>
            <div className="pool-header">
              <div className="pool-icon">🌌</div>
              <h4>Cosmos Pool</h4>
            </div>
            <div className="pool-info">
              <div className="pool-status">
                <span>Status: </span>
                <span className={poolData?.cosmos?.eligible ? 'eligible' : 'not-eligible'}>
                  {poolData?.cosmos?.eligible ? 'Eligible' : 'Not Eligible'}
                </span>
              </div>
              <div className="pool-claimable">
                <span>Claimable: </span>
                <span className="claimable-amount">{formatUSDT(poolData?.cosmos?.claimable || '0')}</span>
              </div>
            </div>
          </div>

          <div className={`pool-card ${poolData?.universe?.eligible ? 'eligible' : 'not-eligible'}`}>
            <div className="pool-header">
              <div className="pool-icon">🪐</div>
              <h4>Universe Pool</h4>
            </div>
            <div className="pool-info">
              <div className="pool-status">
                <span>Status: </span>
                <span className={poolData?.universe?.eligible ? 'eligible' : 'not-eligible'}>
                  {poolData?.universe?.eligible ? 'Eligible' : 'Not Eligible'}
                </span>
              </div>
              <div className="pool-claimable">
                <span>Claimable: </span>
                <span className="claimable-amount">{formatUSDT(poolData?.universe?.claimable || '0')}</span>
              </div>
            </div>
          </div>

          <div className={`pool-card ${poolData?.eternal?.eligible ? 'eligible' : 'not-eligible'}`}>
            <div className="pool-header">
              <div className="pool-icon">♾️</div>
              <h4>Eternal Pool</h4>
            </div>
            <div className="pool-info">
              <div className="pool-status">
                <span>Status: </span>
                <span className={poolData?.eternal?.eligible ? 'eligible' : 'not-eligible'}>
                  {poolData?.eternal?.eligible ? 'Eligible' : 'Not Eligible'}
                </span>
              </div>
              <div className="pool-claimable">
                <span>Claimable: </span>
                <span className="claimable-amount">{formatUSDT(poolData?.eternal?.claimable || '0')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Dashboard;